/**
 * Charting as a printed document — landscape letter, the organization's logo
 * and Deck Studio colors, and the full table: every level under what is on
 * screen, one column per period, every cell tinted met or missed.
 *
 * Operator ruling 2026-08-28: the table page is the document. The aggregate
 * chart pages were removed; the table starts on page one and every
 * continuation page begins below the header band.
 *
 * Every string goes through pdfSafe (WinAnsi law): a non-CP1252 character
 * renders as stretched garbage in jsPDF's standard fonts.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { pdfSafe } from '../MandateNative/exportMemoPdf';
import { fmtMeasure, deltaSuffix } from './measureFormat';

const API = process.env.REACT_APP_API_URL || '';
const PAGE_W = 792; const PAGE_H = 612; // letter landscape, points
const M_L = 40; const M_R = 40; const M_B = 40;
const BAND_H = 58;            // brand header band
const TOP = BAND_H + 24;      // where content may begin on every page

// Deck Studio's defaults, used only when the branding read fails.
const FALLBACK_TOKENS = {
  brand: '1F4A3F', ink: '172B24', paper: 'FFFFFF', surface: 'F4F6F3', muted: '7C8A84',
  good: '1E7A46', warn: 'B45309', bad: 'B42318',
  chart: ['1F4A3F', 'C97A2B', '156082', '7A5195', '2E8B6E', '9AA7B2'], footer: '',
};

const rgb = (hex) => { const h = String(hex || '').replace('#', '').padEnd(6, '0'); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; };
const tint = (hex, amount = 0.85) => rgb(hex).map((c) => Math.round(c + (255 - c) * amount));

export async function loadBranding() {
  let tokens = FALLBACK_TOKENS; let logo = null;
  try {
    const r = await fetch(`${API}/api/deck-studio/branding`, { credentials: 'include' });
    if (r.ok) {
      const b = await r.json();
      tokens = { ...FALLBACK_TOKENS, ...(b.tokens || {}) };
      if (b.has_logo) logo = await loadLogo(b.logo_mime);
    }
  } catch { /* the document still prints, without the logo */ }
  return { tokens, logo };
}

// The logo as a PNG data URL at a printable size. SVG and oversized bitmaps
// are drawn onto a canvas first so jsPDF always receives a raster it accepts.
async function loadLogo(mime) {
  try {
    const r = await fetch(`${API}/api/deck-studio/branding/logo`, { credentials: 'include' });
    if (!r.ok) return null;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = url; });
    const scale = Math.min(1, 600 / Math.max(1, img.naturalWidth || img.width));
    const w = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const h = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    return { data: canvas.toDataURL('image/png'), w, h, mime: String(mime || '') };
  } catch { return null; }
}

/**
 * @param {object} p
 * @param {string} p.orgName
 * @param {string} p.focusTitle          e.g. "ODAG Table 1" or "All areas"
 * @param {string[]} p.path              breadcrumb labels
 * @param {string} p.grainNoun           month | quarter | year
 * @param {string} p.yearLabel           "All years" | "2026"
 * @param {object[]} p.tableRows         [{kind, label, sub, depth, benchmark, unit, buckets}] every level, expanded
 * @param {{key,label}[]} p.tableColumns
 */
export async function exportChartingPdf(p) {
  const { tokens, logo } = await loadBranding();
  const C = {
    brand: rgb(tokens.brand), ink: rgb(tokens.ink), muted: rgb(tokens.muted),
    good: rgb(tokens.good), bad: rgb(tokens.bad), warn: rgb(tokens.warn), line: [226, 232, 240],
    // Case-type lines take Deck Studio's chart palette after the brand color.
    palette: (tokens.chart || FALLBACK_TOKENS.chart).slice(1).map(rgb),
  };
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'landscape' });
  const orgName = pdfSafe(p.orgName || '');
  const footerText = pdfSafe(tokens.footer || p.orgName || '');
  const stamp = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const t = (s) => pdfSafe(s);
  const fv = (v, u) => fmtMeasure(v, u || 'percent');

  // ── Page furniture, drawn on every page ────────────────────────────────
  const header = () => {
    doc.setFillColor(...C.brand); doc.rect(0, 0, PAGE_W, BAND_H, 'F');
    let x = M_L;
    if (logo) {
      const boxW = 120; const boxH = 38; const pad = 5;
      doc.setFillColor(255, 255, 255); doc.roundedRect(x, 10, boxW, boxH, 4, 4, 'F');
      const s = Math.min((boxW - pad * 2) / logo.w, (boxH - pad * 2) / logo.h);
      const w = logo.w * s; const h = logo.h * s;
      try { doc.addImage(logo.data, 'PNG', x + (boxW - w) / 2, 10 + (boxH - h) / 2, w, h); } catch { /* skip a logo jsPDF cannot place */ }
      x += boxW + 14;
    }
    // Operator ruling 2026-08-28: the band carries the title only; the scope
    // line ("All areas - by month - All years") is gone.
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
    doc.text(t(p.docTitle ? `${p.docTitle}` : 'Oversight Charting'), x, 35);
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(orgName, PAGE_W - M_R, 24, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.text(t(stamp), PAGE_W - M_R, 38, { align: 'right' });
  };
  // The footer names the client only, never the product.
  const footer = (pageNo) => {
    doc.setDrawColor(...C.line); doc.setLineWidth(0.6); doc.line(M_L, PAGE_H - M_B + 6, PAGE_W - M_R, PAGE_H - M_B + 6);
    doc.setTextColor(...C.muted); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(footerText, M_L, PAGE_H - M_B + 18);
    doc.text(t(`Page ${pageNo}`), PAGE_W - M_R, PAGE_H - M_B + 18, { align: 'right' });
  };

  // ── The table ──────────────────────────────────────────────────────────
  header();
  let y = TOP;
  doc.setTextColor(...C.ink); doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text(t(`${p.focusTitle} by ${p.grainNoun}, everything underneath`), M_L, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...C.muted);
  doc.text(t('Rows are program areas, measures, and case types.'), M_L, y + 12);
  y += 24;

  const cols = p.tableColumns;
  const head = [['Name', 'Benchmark', ...cols.map((c) => c.label), 'Latest', 'Change']];
  const body = p.tableRows.map((r) => {
    const byKey = new Map(r.buckets.map((b) => [b.key, b]));
    const c1 = r.buckets[r.buckets.length - 1]; const c0 = r.buckets[r.buckets.length - 2];
    const d = c1 && c0 && c1.rate !== null && c0.rate !== null ? Math.round((c1.rate - c0.rate) * 10) / 10 : null;
    return [
      { content: t(`${'   '.repeat(r.depth)}${r.label}${r.sub ? `  (${r.sub})` : ''}`), styles: { fontStyle: r.depth === 0 ? 'bold' : 'normal' } },
      r.benchmark === null || r.benchmark === undefined ? '-' : fv(r.benchmark, r.unit),
      ...cols.map((c) => { const b = byKey.get(c.key); return b && b.rate !== null ? { content: fv(b.rate, r.unit), _ok: b.ok } : { content: '', _ok: null }; }),
      c1 && c1.rate !== null ? fv(c1.rate, r.unit) : '-',
      d === null ? '-' : `${d >= 0 ? '+' : ''}${d}${deltaSuffix(r.unit)}`,
    ];
  });
  autoTable(doc, {
    startY: y, head, body,
    // Every page keeps the band clear: rows never start under the header.
    margin: { top: TOP, left: M_L, right: M_R, bottom: M_B + 14 },
    showHead: 'everyPage',
    styles: { font: 'helvetica', fontSize: 7.5, cellPadding: 3, textColor: C.ink, lineColor: C.line, lineWidth: 0.4, halign: 'right' },
    headStyles: { fillColor: C.brand, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right' },
    columnStyles: { 0: { halign: 'left', cellWidth: 190 }, 1: { cellWidth: 52 } },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const raw = data.cell.raw;
      if (raw && typeof raw === 'object' && '_ok' in raw) {
        if (raw._ok === false) { data.cell.styles.fillColor = tint(tokens.bad, 0.86); data.cell.styles.textColor = C.bad; data.cell.styles.fontStyle = 'bold'; }
        else if (raw._ok === true) { data.cell.styles.fillColor = tint(tokens.good, 0.86); data.cell.styles.textColor = C.good; data.cell.styles.fontStyle = 'bold'; }
      }
      if (data.column.index === 0) data.cell.styles.halign = 'left';
    },
    // autoTable adds continuation pages itself; the band and footer go on each.
    didDrawPage: (data) => { if (data.pageNumber > 1) header(); footer(data.pageNumber); },
  });

  // ── Then the same tree, one page per measure: its trend, its case types ──
  // (operator ruling 2026-08-28: table first, then walk the tree; program
  // areas get no chart page, nor does a measure with nothing scored).
  const measures = groupMeasures(p.tableRows);
  for (const m of measures) {
    if (!m.buckets.some((b) => b.rate !== null && b.rate !== undefined)) continue;
    doc.addPage(); header();
    drawMeasurePage(doc, C, tokens, m, { grainNoun: p.grainNoun, fv, t });
    footer(doc.internal.getNumberOfPages());
  }

  const name = `${p.docTitle || 'COA Charting'} - ${p.focusTitle} - by ${p.grainNoun}${p.yearLabel !== 'All years' ? ` ${p.yearLabel}` : ''}.pdf`;
  doc.save(pdfSafe(name).replace(/[\\/:*?"<>|]/g, '-'));
}

/** Measures with the case-type rows that follow them, in table order. */
function groupMeasures(rows) {
  const out = [];
  for (const r of rows) {
    if (r.kind === 'Measure') out.push({ ...r, cases: [] });
    else if (r.kind === 'Case type' && out.length) out[out.length - 1].cases.push(r);
  }
  return out;
}

const CASE_PALETTE_FALLBACK = [[201, 122, 43], [21, 96, 130], [122, 81, 149], [46, 139, 110], [154, 167, 178], [219, 39, 119]];
const WHITE = [255, 255, 255];
const mix = (c, amount = 0.85) => c.map((v) => Math.round(v + (255 - v) * amount));

/** A rounded label: filled chip with centered text. Returns its width. */
function chip(doc, x, y, text, { fill, color, size = 7.5, bold = true, padX = 6, h = 13, align = 'left' }) {
  doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(size);
  const w = doc.getTextWidth(text) + padX * 2;
  const x0 = align === 'right' ? x - w : align === 'center' ? x - w / 2 : x;
  doc.setFillColor(...fill); doc.roundedRect(x0, y - h + 3.5, w, h, h / 2, h / 2, 'F');
  doc.setTextColor(...color); doc.text(text, x0 + padX, y);
  return w;
}

/**
 * One measure, one page: a headline strip (latest result, change, benchmark,
 * case types), then the trend on a card — area under the measure, each case
 * type as its own line, the benchmark as a dashed guide, the latest value
 * called out — and a legend that says where each line stands today.
 */
function drawMeasurePage(doc, C, tokens, m, { grainNoun, fv, t }) {
  const unit = m.unit || 'percent'; const isPct = unit === 'percent';
  const bench = m.benchmark === null || m.benchmark === undefined ? null : Number(m.benchmark);
  const cur = m.buckets[m.buckets.length - 1] || null;
  const prev = m.buckets[m.buckets.length - 2] || null;
  const delta = cur && prev && cur.rate !== null && prev.rate !== null ? Math.round((cur.rate - prev.rate) * 10) / 10 : null;
  const lower = m.direction === 'lower';
  const deltaGood = delta === null ? null : (lower ? delta <= 0 : delta >= 0);

  // Title
  let y = TOP;
  doc.setTextColor(...C.ink); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text(t(m.label), M_L, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.muted);
  doc.text(t(`${m.sub ? `${m.sub}  ·  ` : ''}${isPct ? 'Rate of cases within the standard' : 'The measured result'}, ${grainNoun} over ${grainNoun}`), M_L, y + 14);
  y += 30;

  // Headline strip: four cards
  const gap = 10; const cardW = (PAGE_W - M_L - M_R - gap * 3) / 4; const cardH = 54;
  const cards = [
    { label: `Latest ${grainNoun}`, value: cur && cur.rate !== null ? fv(cur.rate, unit) : '-',
      tone: cur?.ok === false ? C.bad : cur?.ok ? C.good : C.ink,
      pill: cur ? (cur.ok === false ? ['Missed', C.bad] : cur.ok ? ['Met', C.good] : null) : null, sub: cur ? cur.label : '' },
    { label: `Change vs prior ${grainNoun}`, value: delta === null ? '-' : `${delta > 0 ? '+' : ''}${delta}${deltaSuffix(unit)}`,
      tone: deltaGood === null ? C.ink : deltaGood ? C.good : C.bad, sub: prev ? `from ${fv(prev.rate, unit)} in ${prev.label}` : 'no prior period' },
    { label: 'Benchmark', value: bench === null ? 'none' : fv(bench, unit), tone: C.brand,
      sub: bench === null ? 'results reported without a standard' : (lower ? 'at or below passes' : 'at or above passes') },
    { label: 'Case types', value: String(m.cases.length || 1), tone: C.ink,
      sub: m.cases.length ? m.cases.map((c) => c.label).join(', ').slice(0, 60) : 'one line, the measure itself' },
  ];
  cards.forEach((c, i) => {
    const x = M_L + i * (cardW + gap);
    doc.setFillColor(...mix(C.brand, 0.94)); doc.roundedRect(x, y, cardW, cardH, 8, 8, 'F');
    doc.setTextColor(...C.muted); doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
    doc.text(t(c.label.toUpperCase()), x + 12, y + 14);
    doc.setTextColor(...c.tone); doc.setFontSize(18);
    doc.text(t(c.value), x + 12, y + 35);
    if (c.pill) chip(doc, x + 12 + doc.getTextWidth(t(c.value)) + 8, y + 33, t(c.pill[0]), { fill: mix(c.pill[1], 0.85), color: c.pill[1] });
    doc.setTextColor(...C.muted); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    doc.text(t(c.sub), x + 12, y + 47);
  });
  y += cardH + 16;

  // Chart card
  const legendH = 22;
  const card = { x: M_L, y, w: PAGE_W - M_L - M_R, h: PAGE_H - M_B - 22 - y };
  doc.setDrawColor(...C.line); doc.setLineWidth(0.6); doc.setFillColor(...WHITE);
  doc.roundedRect(card.x, card.y, card.w, card.h, 10, 10, 'FD');

  const labels = m.buckets.map((b) => b.label);
  const palette = (C.palette && C.palette.length ? C.palette : CASE_PALETTE_FALLBACK);
  const series = [
    { label: m.label, values: m.buckets.map((b) => b.rate), color: C.brand, width: 2.4, main: true,
      marks: m.buckets.map((b) => (b.ok === false ? C.bad : b.ok ? C.good : C.brand)), last: cur },
    ...m.cases.map((c, i) => {
      const byKey = new Map(c.buckets.map((b) => [b.key, b]));
      const last = c.buckets[c.buckets.length - 1] || null;
      return { label: c.label, values: m.buckets.map((b) => byKey.get(b.key)?.rate ?? null), color: palette[i % palette.length], width: 1.5, last };
    }),
  ];
  const vals = series.flatMap((s) => s.values).filter((v) => v !== null && v !== undefined);
  const lo = Math.min(...(vals.length ? vals : [0]), bench ?? (isPct ? 100 : 0));
  const hi = Math.max(...(vals.length ? vals : [1]), bench ?? 0);
  const yMin = isPct ? Math.max(0, Math.floor(Math.min(lo, (bench ?? 80) - 10) / 10) * 10) : 0;
  const yMax = isPct ? 100 : Math.max(1, Math.ceil(hi * 1.15));

  const plotL = card.x + 52; const plotR = card.x + card.w - 70; const plotT = card.y + 22; const plotB = card.y + card.h - legendH - 30;
  const yOf = (v) => plotB - ((Math.min(yMax, Math.max(yMin, v)) - yMin) / Math.max(1, yMax - yMin)) * (plotB - plotT);
  const n = Math.max(1, labels.length);
  const xOf = (i) => (n === 1 ? (plotL + plotR) / 2 : plotL + (i / (n - 1)) * (plotR - plotL));

  // Quiet grid: four horizontal guides, labels at the left, periods below.
  doc.setDrawColor(...mix(C.line, 0.3)); doc.setLineWidth(0.5); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...C.muted);
  for (let s = 0; s <= 4; s++) {
    const v = yMin + ((yMax - yMin) * s) / 4; const yy = yOf(v);
    doc.line(plotL, yy, plotR, yy);
    doc.text(pdfSafe(isPct ? `${Math.round(v)}%` : fv(v, unit)), plotL - 6, yy + 2.5, { align: 'right' });
  }
  const every = Math.max(1, Math.ceil(n / 14));
  labels.forEach((l, i) => { if (i % every === 0 || i === n - 1) doc.text(pdfSafe(l), xOf(i), plotB + 12, { align: 'center' }); });

  // Area under the measure line (brand tint), then the lines.
  const main = series[0];
  const pts = main.values.map((v, i) => (v === null || v === undefined ? null : [xOf(i), yOf(v)]));
  const runs = []; let run = [];
  for (const pt of pts) { if (pt) run.push(pt); else if (run.length) { runs.push(run); run = []; } }
  if (run.length) runs.push(run);
  for (const r of runs) {
    if (r.length < 2) continue;
    const poly = [...r, [r[r.length - 1][0], plotB], [r[0][0], plotB]];
    const segs = []; for (let i = 1; i < poly.length; i++) segs.push([poly[i][0] - poly[i - 1][0], poly[i][1] - poly[i - 1][1]]);
    doc.setFillColor(...mix(C.brand, 0.9)); doc.lines(segs, poly[0][0], poly[0][1], [1, 1], 'F', true);
  }
  // Benchmark guide with a chip at its right end.
  if (bench !== null) {
    doc.setDrawColor(...C.warn); doc.setLineWidth(1); doc.setLineDashPattern([5, 3], 0);
    doc.line(plotL, yOf(bench), plotR, yOf(bench)); doc.setLineDashPattern([], 0);
    chip(doc, plotR + 6, yOf(bench) + 4, pdfSafe(`Benchmark ${fv(bench, unit)}`), { fill: mix(C.warn, 0.85), color: C.warn, size: 7 });
  }
  for (const s of [...series].reverse()) {
    doc.setDrawColor(...s.color); doc.setLineWidth(s.width);
    let prevPt = null;
    s.values.forEach((v, i) => {
      if (v === null || v === undefined) { prevPt = null; return; }
      const pt = [xOf(i), yOf(v)];
      if (prevPt) doc.line(prevPt[0], prevPt[1], pt[0], pt[1]);
      prevPt = pt;
    });
    s.values.forEach((v, i) => {
      if (v === null || v === undefined) return;
      const fill = s.marks ? s.marks[i] : s.color;
      doc.setFillColor(...WHITE); doc.setDrawColor(...fill); doc.setLineWidth(s.main ? 1.6 : 1.2);
      doc.circle(xOf(i), yOf(v), s.main ? 3.2 : 2.2, 'FD');
      doc.setFillColor(...fill); doc.circle(xOf(i), yOf(v), s.main ? 1.6 : 1.1, 'F');
    });
  }
  // The latest measure value, called out.
  const lastIdx = main.values.map((v, i) => (v === null || v === undefined ? -1 : i)).filter((i) => i >= 0).pop();
  if (lastIdx !== undefined) {
    const v = main.values[lastIdx]; const tone = main.marks[lastIdx];
    chip(doc, xOf(lastIdx), yOf(v) - 9, pdfSafe(fv(v, unit)), { fill: tone, color: WHITE, size: 7.5, align: 'center' });
  }

  // Legend: each line with where it stands today.
  let lx = plotL - 40; const ly = card.y + card.h - 9;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  for (const s of series) {
    const last = s.last; const state = last && last.ok === false ? 'missed' : last && last.ok ? 'met' : null;
    const label = pdfSafe(`${s.label}${last && last.rate !== null && last.rate !== undefined ? `  ${fv(last.rate, unit)}` : ''}`);
    doc.setFillColor(...s.color); doc.circle(lx + 4, ly - 2.5, 3, 'F');
    doc.setTextColor(...C.ink); doc.setFont('helvetica', s.main ? 'bold' : 'normal'); doc.text(label, lx + 11, ly);
    lx += 11 + doc.getTextWidth(label) + 6;
    if (state) { lx += chip(doc, lx, ly, state === 'met' ? 'Met' : 'Missed', { fill: mix(state === 'met' ? C.good : C.bad, 0.85), color: state === 'met' ? C.good : C.bad, size: 6.5, h: 11, padX: 5 }) + 10; }
    else lx += 10;
    if (lx > card.x + card.w - 90) break;
  }
}
